import { Component, OnDestroy, OnInit } from '@angular/core';
import { DeviceCartsFacade } from '@kion/data-access/data-access-device-carts';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import { debounce, map, take } from 'rxjs/operators';
import { Device, DeviceCart, DeviceFeature, Role } from '@kion/data-models';
import {
  BehaviorSubject,
  combineLatest,
  from,
  interval,
  Observable, of,
} from 'rxjs';
import { DevicesFacade } from '@kion/data-access/data-access-devices';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { Checkbox } from '../../../../../../shared/ui/src/lib/shared/elements/checkbox/checkbox.component';
import { TranslateService } from '@ngx-translate/core';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SubSink } from 'subsink';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { AlertDialogComponent } from '../../../../../../shared/ui/src/lib/shared/dialogs/alert-dialog/alert-dialog.component';
import { RolesFacade } from '@kion/data-access/data-access-roles';
import { UsersFacade } from '@kion/data-access/data-access-users';
import { ToastService } from '@kion/ui';
import { FeatureEnablementsFacade } from '@kion/data-access/data-access-feature-enablements';
import { LmhSoftwareProductsFacade } from '@kion/data-access/data-access-lmh-software-products';
import {ToastType} from "@kion/libs/shared/ui/src/lib/shared/toasts/config/toast.config";

@Component({
  selector: 'kion-cms-quote',
  templateUrl: './quote.component.html',
  styleUrls: ['./quote.component.scss'],
})
export class QuoteComponent implements OnInit, OnDestroy {
  private factoryId: string;
  private deviceId: string;
  private cartId: string;

  deviceCart$: Observable<DeviceCart>;
  device$: Observable<Device>;
  isBuyable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  roles$: Observable<Role[]>;
  features$: Observable<DeviceFeature[]>;
  softwareProducts$: Observable<DeviceFeature[]>;
  deviceFeatures$: Observable<DeviceFeature[]>;
  isAddCcRecipientButtonEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  ccEmails$: Observable<String[]>;

  boxes: Checkbox[];
  additionalCosts: Checkbox;
  customersConsent: Checkbox;

  form: FormGroup;
  formCcRecipients: FormGroup;
  subSink: SubSink;
  unhealthy$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isBuying: boolean;

  EMAIL_ADDRESS_REGEX = new RegExp('^(?=.{1,64}@)[A-Za-z0-9_-]+(\\.[A-Za-z0-9_-]+)*@[^-][A-Za-z0-9-]+(\\.[A-Za-z0-9-]+)*(\\.[A-Za-z]{2,})$');

  constructor(
    private devicesFacade: DevicesFacade,
    private deviceCartsFacade: DeviceCartsFacade,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private observer: CorrelationObserverService,
    private ngbModal: NgbModal,
    private router: Router,
    private rolesFacade: RolesFacade,
    private usersFacade: UsersFacade,
    private toastService: ToastService,
    private featureEnablementsFacade: FeatureEnablementsFacade,
    private lmhSoftwareProductsFacade: LmhSoftwareProductsFacade,
    formBuilder: FormBuilder
  ) {
    this.subSink = new SubSink();
    this.form = formBuilder.group({
      poNumber: new FormControl(null),
      poNote: new FormControl(null),
    });
    this.formCcRecipients = formBuilder.group({
      poCcRecipients: new FormControl(null, [Validators.required,
        Validators.pattern(this.EMAIL_ADDRESS_REGEX)]),
    });
  }

  ngOnInit(): void {
    const routeSnapshot: ActivatedRouteSnapshot = this.route.snapshot;
    this.factoryId = routeSnapshot.params.factoryId;
    this.deviceId = routeSnapshot.params.deviceId;
    this.cartId = routeSnapshot.params.id;
    this.devicesFacade.loadById(this.factoryId, this.deviceId);
    this.deviceCartsFacade.loadCartByDevice(this.factoryId, this.deviceId);
    this.featureEnablementsFacade.loadAll(this.deviceId, this.factoryId);
    this.lmhSoftwareProductsFacade.loadAll(this.deviceId);
    this.features$ = this.featureEnablementsFacade.all$;
    this.softwareProducts$ = this.lmhSoftwareProductsFacade.all$;
    this.initAddCcRecipientButton();
    this.deviceCart$ = this.deviceCartsFacade.all$.pipe(
      map((deviceCarts: DeviceCart[]) =>
        deviceCarts.find(
          (deviceCart: DeviceCart) => deviceCart.deviceId === this.deviceId
        )
      )
    );
    this.device$ = this.devicesFacade.all$.pipe(
      map((devices: Device[]) =>
        devices.find(
          (device: Device) =>
            device.manufacturer.toLowerCase() === this.factoryId &&
            device.id === this.deviceId
        )
      )
    );
    this.subSink.sink = this.deviceCart$.subscribe((cart: DeviceCart) => {
      if (cart) {
        const poNumber = this.form.get('poNumber');
        const poNote = this.form.get('poNote');
        if (!poNumber.value) {
          poNumber.patchValue(cart.purchaseOrderNumber);
        }
        if (!poNote.value) {
          poNote.patchValue(cart.userComment?.text);
        }
        const ccEmails = cart.additionalCcEmails ? [...cart.additionalCcEmails] : [];
        this.ccEmails$ = of(ccEmails.sort());
      }
    });
    this.subSink.sink = combineLatest([
      this.form.valueChanges,
      this.deviceCart$,
    ])
       .pipe(debounce(() => interval(500)))
       .subscribe(([values, cart]) => {
         if (
            values.poNote !== cart.userComment?.text ||
            values.poNumber !== cart.purchaseOrderNumber
         ) {
           this.updateQuote(null, values.poNote || '', values.poNumber || '');
         }
       });
    this.subSink.sink = this.usersFacade.all$.subscribe((users) => {
      const me = users.find((user) => user.id === '1');
      this.rolesFacade.loadByUserId(me.uid, true);
    });
    this.roles$ = combineLatest([this.device$, this.rolesFacade.all$]).pipe(
      map(([device, roles]) => {
        if (device) {
          return roles.filter(
            (role: Role) => role.baseStore === device.networkPartner.uid
          );
        }
      })
    );
    this.deviceFeatures$ = combineLatest([
      this.features$,
      this.softwareProducts$,
    ]).pipe(map(([features, softProducts]) => features.concat(softProducts)));
    this.subSink.sink = combineLatest([this.deviceFeatures$, this.deviceCart$])
      .pipe(debounce(() => interval(500)))
      .subscribe(([features, cart]) => {
        const softwareProducts = features.filter((feature) =>
          cart.entries.find(
            (entry) =>
              entry.product.code === feature.id && feature.truckHealthManagement
          )
        );
        const softwareFeatures = features.filter((feature) =>
          cart.entries.find(
            (entry) =>
              entry.product.code === feature.id &&
              !feature.truckHealthManagement
          )
        );
        this.loadCheckboxes();
        let unHealthyFeatures = [];
        let unHealthySoftwareFeatures = [];
        let unHealthySoftwareProducts = [];
        if (softwareFeatures.length > 0) {
          unHealthySoftwareFeatures = softwareFeatures.filter(
            (feature) => feature.status !== 'AVAILABLE'
          );
          this.boxes = this.boxes.concat(this.additionalCosts);
        }
        if (softwareProducts.length > 0) {
          unHealthySoftwareProducts = softwareProducts.filter(
            (feature) => feature.truckHealthManagement?.status !== 'AVAILABLE'
          );
          this.boxes = this.boxes.concat(this.customersConsent);
        }
        unHealthyFeatures = unHealthySoftwareFeatures.concat(
          unHealthySoftwareProducts
        );
        if (unHealthyFeatures.length > 0) {
          //TODO: translations and action button
          this.showMessage('basket.toast.features.incompatible.error', 'error');
          this.unhealthy$.next(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  private initAddCcRecipientButton() {
    this.formCcRecipients.statusChanges.subscribe((status) => {
      switch (status) {
        case 'VALID':
          this.isAddCcRecipientButtonEnabled$.next(true);
          return;
        case 'INVALID':
        default:
          this.isAddCcRecipientButtonEnabled$.next(false);
          return;
      }
    });
  }

  toggleBuyable(isChecked: boolean) {
    this.isBuyable.next(isChecked);
  }

  buy() {
    this.isBuying = true;
    this.subSink.sink = this.observer.subscribeToAction(
      this.deviceCartsFacade.placeOrder(this.cartId),
      () => {
        this.showMessage('basket.toast.buy.success', 'success');
        this.deviceCartsFacade.delete(this.factoryId, this.deviceId, this.cartId);
        this.clearDeviceState();
        this.subSink.sink = this.deviceCart$
          .pipe(take(1))
          .subscribe((cart) =>
            this.router
              .navigate(['basket', 'confirmation', cart.orderNumber])
              .then(() => (this.isBuying = false))
          );
      },
      () => {
        this.showMessage('basket.toast.buy.error', 'error');
        this.isBuying = false;
      }
    );
  }

  updateStatus(status: string): void {
    this.updateQuote(status.toUpperCase());
  }

  deleteFeature(entryNumber: number): void {
    const dialogRef = this.ngbModal.open(AlertDialogComponent);
    dialogRef.componentInstance.data = {
      headline: this.translateService.instant('basket.dialog.delete.feature.headline'),
      body: this.translateService.instant('basket.dialog.delete.feature.body'),
      btnCancel: this.translateService.instant('basket.dialog.delete.feature.button.cancel'),
      btnSubmit: this.translateService.instant('basket.dialog.delete.feature.button.submit'),
    };
    this.subSink.sink = from(dialogRef.result).subscribe(
      (hasAgreed: boolean) => {
        if (hasAgreed) {
          this.subSink.sink = this.observer.subscribeToAction(
            this.deviceCartsFacade.removeFromCart(
              this.factoryId,
              this.deviceId,
              this.cartId,
              entryNumber
            ),
            () => {
              this.showMessage('basket.toast.delete.feature.success', 'success');
              this.deviceCartsFacade.loadCartByDevice(
                this.factoryId,
                this.deviceId
              );
            },
            () => {
              this.showMessage('basket.toast.delete.feature.error', 'error');
            }
          );
        }
      }
    );
  }

  deleteQuote() {
    const dialogRef = this.ngbModal.open(AlertDialogComponent);
    dialogRef.componentInstance.data = {
      headline: this.translateService.instant('basket.dialog.delete.quote.headline'),
      body: this.translateService.instant('basket.dialog.delete.quote.body'),
      btnCancel: this.translateService.instant('basket.dialog.delete.quote.button.cancel'),
      btnSubmit: this.translateService.instant('basket.dialog.delete.quote.button.submit'),
    };
    this.subSink.sink = from(dialogRef.result).subscribe(
      (hasAgreed: boolean) => {
        if (hasAgreed) {
          this.subSink.sink = this.observer.subscribeToAction(
            this.deviceCartsFacade.delete(
              this.factoryId,
              this.deviceId,
              this.cartId
            ),
            () => {
              this.showMessage('basket.toast.delete.quote.success', 'success');
              this.clearDeviceState();
              this.router
                .navigate(['device-list', this.factoryId, this.deviceId])
                .then(() => {});
            },
            () => {
              this.showMessage('basket.toast.delete.quote.error', 'error');
            }
          );
        }
      }
    );
  }

  addCcRecipient(event: MouseEvent): void {
    event.stopPropagation();
    this.updateQuoteWithCcRecipient(this.formCcRecipients.controls['poCcRecipients']?.value);
  }

  deleteCcRecipient(ccEmail: string, event: MouseEvent) {
    event.stopPropagation();
    this.updateQuoteWithCcRecipient(ccEmail, true);
  }

  private clearDeviceState() {
    this.subSink.sink = this.device$
      .pipe(take(1))
      .subscribe((device: Device) =>
        this.devicesFacade.removeDevicesFromStore([device], this.deviceId)
      );
    this.devicesFacade.loadById(this.factoryId, this.deviceId);
    this.deviceCartsFacade.loadCartByDevice(this.factoryId, this.deviceId);
  }

  get hasApproverRole$(): Observable<boolean> {
    return this.roles$.pipe(
      map((roles) => {
        if (!roles) {
          return false;
        }
        return roles.map((role) => role.role.uid).includes('fsNwpApprover');
      })
    );
  }

  get hasBuyerRole$(): Observable<boolean> {
    return this.roles$.pipe(
      map((roles) => {
        if (!roles) {
          return false;
        }
        return roles.map((role) => role.role.uid).includes('fsNwpBuyer');
      })
    );
  }

  private updateQuote(
    status?: string,
    comment?: string,
    purchaseOrderNumber?: string
  ): void {
    this.subSink.sink = combineLatest([this.deviceCart$, this.device$])
      .pipe(take(1))
      .subscribe(([cart, device]) => {
        this.subSink.sink = this.observer.subscribeToAction(
          this.deviceCartsFacade.update(
            this.factoryId,
            this.deviceId,
            device.quote.code,
            status ? status : device.quote.status,
            typeof comment ? comment : cart.userComment?.text,
            typeof purchaseOrderNumber
              ? purchaseOrderNumber
              : cart.purchaseOrderNumber,
            cart.additionalCcEmails,
            cart.purchaseOrderCreationDateString,
            cart.id
          ),
          () => {
            this.showMessage('basket.toast.update.quote.success', 'success');
            this.devicesFacade.loadById(this.factoryId, this.deviceId);
            this.deviceCartsFacade.loadCartByDevice(
              this.factoryId,
              this.deviceId
            );
          },
          () => {
            this.showMessage('basket.toast.update.quote.error', 'error');
          }
        );
      });
  }

  private updateQuoteWithCcRecipient(ccEmail?: string, deleteIt?: boolean): void {
    this.subSink.sink = combineLatest([this.deviceCart$, this.device$])
      .pipe(take(1))
      .subscribe(([cart, device]) => {
        this.subSink.sink = this.observer.subscribeToAction(
           this.deviceCartsFacade.update(
              this.factoryId,
              this.deviceId,
              device.quote.code,
              device.quote.status,
              cart.userComment?.text,
              cart.purchaseOrderNumber,
              deleteIt
                 ? cart.additionalCcEmails.filter(item => item !== ccEmail)
                 : cart.additionalCcEmails ? [...cart.additionalCcEmails, ccEmail] : [ccEmail],
              cart.purchaseOrderCreationDateString,
              cart.id
           ),
          () => {
            this.showMessage('basket.toast.update.quote.success', 'success');
            this.formCcRecipients.get('poCcRecipients').patchValue('');
            this.devicesFacade.loadById(this.factoryId, this.deviceId);
            this.deviceCartsFacade.loadCartByDevice(this.factoryId, this.deviceId);
          },
          () => {
            this.showMessage('basket.toast.update.quote.error', 'error');
          }
        );
      });
  }

  private showMessage(msg: string, type: ToastType) {
    this.toastService.show({
      text: this.translateService.instant(msg) ,
      type: type,
    });
  }

  private loadCheckboxes() {
    this.customersConsent = {
      title: this.translateService.instant(
        'basket.summary.checkbox.consent.headline',
        {
          link: '<a target="_blank" href="' + this.translateService.instant('basket.summary.checkbox.consent.href') +
            '">' + this.translateService.instant('basket.summary.checkbox.consent.link') + '</a>',
        }
      ),
      isLink: false,
      required: true,
      checked: false,
      titleAsHtml: true,
    };
    this.additionalCosts = {
      title: this.translateService.instant('basket.summary.checkbox.additionalCost.headline'),
      isLink: false,
      required: true,
      checked: false,
    };
    this.boxes = [
      {
        title: this.translateService.instant(
          'basket.summary.checkbox.terms.headline',
          {
            termsLink: '<a href="terms-and-conditions" target="_blank">  ' + this.translateService.instant(
                'basket.summary.checkbox.terms.termsLink') + ' </a>',
            policyLink: '<a href="privacy-policy" target="_blank">  ' + this.translateService.instant(
                'basket.summary.checkbox.terms.policyLink') + ' </a>',
          }
        ),
        isLink: false,
        required: true,
        checked: false,
        titleAsHtml: true,
      },
    ];
  }
}
